import { useEffect, useState, type ReactElement } from 'react';
import { trackFailed } from '../../../../../shared-web/track';
import EmberFlame from '../../../../../shared-web/EmberFlame';
import { ApiError } from '../../auth/api-client';
import { emberApi, type HandoffResponse } from '../api';
import { provenanceLine, splitParagraphs } from '../format';
import { EmberPhotoRow, photosUnder } from '../components/EmberPhotos';
import { useTalkSheet } from '../talk-sheet';
import { EmberMediaHeader } from '../components/EmberMediaHeader';
import { PageFailure } from '../components/PageFailure';
import { PageLoading } from '../components/PageLoading';
import './HandoffPage.css';

/**
 * S17 — the hand-off page `/e/:token` (v4.0 §4.18, 2026-09-06 拍板): an Ember
 * its owner sent to this person. It is the Ember itself — cover, receipt,
 * title, the whole text — and at the end one door: tell yours. No presence
 * (§0.9: a stable identity stays inside the access boundary even when the
 * owner hands the page out), no status, no registration ask (that is the
 * owner's, on Result), nothing from the v3.0 network.
 *
 * A link the owner took back reads as exactly that, with the same one door.
 */
export function HandoffPage({ token, initialData = null }: {
  token: string;
  initialData?: HandoffResponse | null;
}): ReactElement {
  const openTalk = useTalkSheet();
  const path = `/e/${encodeURIComponent(token)}`;
  const [data, setData] = useState<HandoffResponse | null>(initialData);
  const [failed, setFailed] = useState<false | 'gone' | 'load_failed'>(false);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    emberApi.handoff(token)
      .then((response) => { if (alive) setData(response); })
      .catch((err) => {
        if (!alive || initialData) return;
        const gone = err instanceof ApiError && err.status === 404;
        if (!gone) trackFailed('handoff_load', 'load_failed');
        setFailed(gone ? 'gone' : 'load_failed');
      });
    return () => { alive = false; };
  }, [token, initialData]);

  const door = (
    <section className="handoff-door" aria-labelledby="handoff-door-title">
      <h2 id="handoff-door-title" className="handoff-door-title">
        <EmberFlame className="handoff-door-flame" />
        You have one too.
      </h2>
      <p className="handoff-door-text">Say the thing you keep coming back to. Ember won’t pretend there’s a right answer — it helps you hear what you actually think. You decide.</p>
      <div className="handoff-door-foot">
        <button
          type="button"
          className="handoff-door-cta"
          data-log="handoff_tell_open"
          data-log-ember-id={data?.ember.id}
          onClick={() => openTalk({ entryType: 'home', returnTo: path })}
        >
          Tell yours
        </button>
        <span className="handoff-door-note">Type or talk. No account needed.</span>
      </div>
    </section>
  );

  const brand = (
    <div className="handoff-toolbar">
      <a href="/" className="handoff-brand" aria-label="Ember home" data-log="page_home">
        <EmberFlame className="handoff-brand-flame" />
        <span>Ember</span>
      </a>
    </div>
  );

  if (failed === 'gone') {
    return (
      <main className="ember-container ember-reading handoff-page">
        {brand}
        <div className="handoff-gone" data-log-seen="handoff_seen" data-log-state="gone">
          {/* The edge cannot tell a link taken back from one that never was (taking it back clears the token), so the words hold for both (2026-10-18). */}
          <h1>This Ember isn’t here.</h1>
          <p>The person who told it may have closed the link, or the link isn’t complete.</p>
        </div>
        {door}
      </main>
    );
  }
  if (failed) return <PageFailure title="Couldn't open this Ember" detail="Refresh to try again." logState="load_failed" />;
  if (!data) return <PageLoading />;

  const { ember, provenance } = data;
  const receipt = provenanceLine(provenance);
  const paragraphs = splitParagraphs(ember.canonical_text);

  return (
    <main className="ember-container ember-reading handoff-page">
      {brand}
      <EmberMediaHeader
        media={ember.media}
        alt={`${ember.title || 'Untitled Ember'} — illustration`}
        logEmberId={ember.id}
      />
      <header className="handoff-head" data-log-seen="handoff_seen" data-log-ember-id={ember.id}>
        {receipt && <p className="handoff-receipt">{receipt}</p>}
        <h1 className="handoff-title" dir="auto">{ember.title || 'Untitled Ember'}</h1>
      </header>
      <article className="handoff-text">
        {paragraphs.map((p, i) => (
          <div className="handoff-para-block" key={i}>
            <p className="handoff-para" dir="auto">{p.text}</p>
            <EmberPhotoRow photos={photosUnder(ember.photos, p)} all={ember.photos ?? []} emberId={ember.id} />
          </div>
        ))}
      </article>
      {/* The colophon: the house seal — the lamp's amber the Result seals
          wear — and what this is. Ember shaped the telling; the life is the
          person's, so it is never "by Ember". */}
      <p className="handoff-colophon">
        <span className="handoff-seal" aria-hidden><span>told<br />here</span></span>
        <span>Told to Ember by a real person.</span>
      </p>
      {door}
    </main>
  );
}
